import React from "react";
import type { PoolCreationOptions } from "../../types/pools";
import { StatusMessage } from "../ui";
import { cn } from "../../lib/utils";

type Step1Props = {
  competitionCode: string | null;
  seasonCode: string | null;
  onCompetitionChange: (competitionCode: string, seasonCode: string | null) => void;
  isEditMode: boolean;
  poolOptions: PoolCreationOptions | null;
  isLoading: boolean;
  error: string | null;
};

export function Step1_Competition({
  competitionCode,
  seasonCode,
  onCompetitionChange,
  isEditMode,
  poolOptions,
  isLoading,
  error,
}: Step1Props) {
  const sports = poolOptions?.sports ?? [];

  const availableCompetitions = sports.flatMap((sport) =>
    sport.competitions
      .filter(
        (competition) =>
          competition.season_status === "ACTIVE" || competition.season_status === "PLANNED",
      )
      .map((competition) => ({
        ...competition,
        sport_code: sport.sport_code,
        sport_name: sport.sport_name,
      })),
  );

  return (
    <div className="space-y-3">
      {isLoading && <StatusMessage>Loading competition options...</StatusMessage>}
      {error && <StatusMessage variant="error">{error}</StatusMessage>}

      <div className="space-y-2" role="radiogroup" aria-label="Competition">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-100">Competition</h2>
          <p className="text-xs text-slate-500">Season is selected automatically.</p>
        </div>

        {availableCompetitions.length === 0 && !isLoading && !error ? (
          <StatusMessage>No eligible competitions are available right now.</StatusMessage>
        ) : null}

        <div className="grid gap-2">
          {availableCompetitions.map((competition) => {
            const isSelected =
              competitionCode === competition.competition_code &&
              seasonCode === competition.season_code;
            return (
              <button
                key={`${competition.competition_code}-${competition.season_code ?? "current"}`}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-pressed={isSelected}
                disabled={isEditMode || isLoading}
                onClick={() =>
                  onCompetitionChange(competition.competition_code, competition.season_code)
                }
                className={cn(
                  "flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                  isSelected
                    ? "border-brand bg-brand/10 shadow-[0_0_0_1px_rgba(245,166,35,0.35)]"
                    : "border-slate-800 bg-slate-900/70 hover:border-brand/55",
                  (isEditMode || isLoading) && "cursor-not-allowed opacity-70",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate font-display text-base font-bold uppercase leading-tight text-slate-100">
                    {competition.competition_name}
                  </span>
                  <span className="mt-1 block truncate text-xs font-semibold text-slate-500">
                    {competition.sport_name}
                    {competition.season_code ? ` · ${competition.season_code}` : ""}
                  </span>
                </span>
                <span
                  className={cn(
                    "shrink-0 rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide",
                    competition.season_status === "ACTIVE"
                      ? "border-success/50 bg-success-soft text-success"
                      : "border-brand/50 bg-brand/10 text-brand",
                  )}
                >
                  {competition.season_status ?? "Planned"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
